/**
 * Renders one Ask Vera tool result's ROWS beside the model's prose answer (VRG-227).
 *
 * Presentational — same discipline as `AskMessageList.tsx` (see its docblock): nothing
 * here starts a turn or knows about streaming. It reads an `AskResultSet`
 * (`ask/toolResult.ts`) and paints it, plus offers a click-through to the filtered list
 * that produced it (`buildAskDeepLink`).
 *
 * ONE deliberate, narrow exception to "nothing here fetches": `useEntityAvatarSrc`
 * below hydrates a row's own product/owner/certificate photo by id, because a tool
 * result row carries only `{label, value, entity: {kind, id}}` — never an image URL
 * (see `EntityRow`'s docblock for why that rules out reusing `ProductPreview` /
 * `OwnerPreview` directly). This is a read-only, per-row lookup with no bearing on the
 * conversation's turn/streaming state — it cannot block, retry, or affect an answer —
 * so it stays inside the "presentational" contract in spirit even though it is,
 * mechanically, a fetch.
 *
 * Why this exists at all: only a summary STRING reaches the panel today
 * (`AskToolCall.summary`) — the model's narration of the rows, not the rows
 * themselves. A wrong summary is indistinguishable from a right one until the actual
 * numbers are shown beside it.
 *
 * Visual register matches the sibling blocks in `AskMessageList.tsx` (the tool-call
 * chip, Sources, Terms) deliberately: a hairline `primary[100]` border, `body3`/`body4`
 * typography, `text.secondary` for anything that isn't the number itself. This sits
 * under an answer — it must not outshout it.
 */
import React from 'react'

import { ArrowUpRightIcon } from 'lucide-react'

import { Box, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { Avatar } from '@repo/ui'

import { useCertificate, useOwnerQuery, useProduct } from 'hooks'

import { buildAskDeepLink, type AskResultEntity, type AskResultRow, type AskResultSet } from 'ask/toolResult'
// VRG-777 — `products.images` holds stills AND video in one ordered list, so `images[0]`
// is no longer necessarily a still. `getProductThumbnail` returns the first non-video
// entry; shared in @repo/types so this branch is not re-written at each of the ~20 sites
// that read the column (§36).
import { getProductThumbnail } from '@repo/types'

/** Cap on rendered rows — never silently truncate without saying so (see the caption below). */
const MAX_RENDERED_ROWS = 10

/**
 * Singular/plural noun for a metric, used in the unattributed caption.
 *
 * TOTAL over the metric union on purpose — that totality is what turned VRG-567's three
 * new metrics into a compile error here rather than a silent `undefined` destructured
 * into `pluralize`, which would have rendered the caption as "3 undefined".
 *
 * ⚠️ The three MONEY metrics are unreachable through this map today, and the entries
 * exist so that stays a fact rather than a coincidence. `unattributed` is set only by a
 * dimensioned SQL query that found NULL-dimension rows (`runAskTool`); a money metric
 * carries no dimension and one figure, so it never populates that field and this
 * component returns `null` for it before the caption renders. If a money metric ever did
 * reach here, "1 return on Vera" is at least grammatical — a `undefined` would not be.
 */
const METRIC_NOUN: Record<AskResultSet['metric'], [string, string]> = {
  scans: ['scan', 'scans'],
  certificates: ['certificate', 'certificates'],
  owners: ['owner', 'owners'],
  leads: ['lead', 'leads'],
  transfers: ['transfer', 'transfers'],
  return_on_vera: ['return on Vera', 'returns on Vera'],
  value_delivered: ['value figure', 'value figures'],
  vera_spend: ['spend figure', 'spend figures'],
}

const pluralize = (metric: AskResultSet['metric'], count: number): string => {
  const [singular, plural] = METRIC_NOUN[metric]
  return count === 1 ? singular : plural
}

/**
 * The row's own photo, hydrated by id (VRG-227).
 *
 * A tool result row carries only `{label, value, entity: {kind, id}}` — never an image
 * URL, so this is the one place that reaches out for one. Deliberately NOT
 * `ProductPreview` / `OwnerPreview` / `CertificatePreview` (see `EntityRow`'s
 * docblock) and deliberately NOT a bespoke fetch, either — it reuses the same
 * react-query hooks the rest of the platform already uses to fetch one record by id
 * (`useProduct`, `useOwnerQuery`, `useCertificate`), each individually cached
 * (5-minute `staleTime`) and `enabled`-gated so only the hook matching this row's own
 * `entity.kind` actually fires.
 *
 * Returns `undefined` while the fetch is in flight or for a kind with nothing to show
 * — `Avatar`'s own `src`-less fallback (initials, or a person glyph) already covers
 * that state with no layout shift, so this hook never needs to.
 */
const useEntityAvatarSrc = (entity?: AskResultEntity): string | null | undefined => {
  const productId = entity?.kind === 'product' ? entity.id : ''
  const ownerId = entity?.kind === 'owner' ? entity.id : ''
  const certificateId = entity?.kind === 'certificate' ? entity.id : ''

  const product = useProduct({ productId }, { enabled: !!productId })
  const owner = useOwnerQuery({ ownerId }, { enabled: !!ownerId })
  const certificate = useCertificate({ certificateId }, { enabled: !!certificateId })

  if (productId) return getProductThumbnail(product.data?.images) ?? undefined
  if (ownerId) return owner.data?.owner?.avatar
  if (certificateId) return getProductThumbnail(certificate.data?.product?.images) ?? undefined
  return null
}

/**
 * One row that names a first-class record (product / owner / certificate). Uses the
 * shared `@repo/ui` `Avatar` primitive directly, NOT the composed `ProductPreview` /
 * `OwnerPreview` / `CertificatePreview` components — those all require the FULL record
 * (sku, email, quantity, …), which a tool result row does not carry, only `label` +
 * `entity.id` (+, now, the fetched image — see `useEntityAvatarSrc` above).
 * `Avatar`'s own image-with-initials-fallback (the same mechanism `OwnerPreview`
 * already leans on) is the honest fit for what this row actually has: a photo once
 * hydrated, initials before that or if there is none.
 *
 * The WHOLE row is the navigation target when a deep link exists — not just a
 * trailing icon (that read as inert; only the 14px arrow itself was clickable). A row
 * with no link (a dimension whose row can't be drilled into, e.g. `product` grouped by
 * `scans`) renders identically but is never wrapped in a link — no hover, no cursor
 * change, nothing that promises a click that does nothing.
 */
const EntityRow = ({ row, result }: { row: AskResultRow; result: AskResultSet }) => {
  const theme = useTheme()
  const link = buildAskDeepLink(result, row)
  const imageSrc = useEntityAvatarSrc(row.entity)
  // Owners read as people (circular avatar, matching OwnerPreview/CompanyAvatar's own
  // circular-vs-rounded-square distinction); products and certificates read as things.
  const isPerson = row.entity?.kind === 'owner'

  const inner = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
      <Avatar src={imageSrc} name={row.label} size={22} sx={isPerson ? undefined : { borderRadius: '6px' }} />
      <Typography variant="body3" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {row.label}
      </Typography>
      <Typography variant="body3" sx={{ fontWeight: 600, color: theme.palette.text.primary, flexShrink: 0 }}>
        {row.value.toLocaleString()}
      </Typography>
      {link && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          className="ask-row-view"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          <Typography variant="body4" sx={{ fontWeight: 600 }}>
            View
          </Typography>
          <ArrowUpRightIcon strokeWidth={1} size={12} />
        </Stack>
      )}
    </Stack>
  )

  if (!link) return inner

  return (
    <Tooltip title={link.label} placement="top" enterDelay={400}>
      <Box
        component={RouterLink}
        to={link.to}
        aria-label={link.label}
        sx={{
          display: 'block',
          color: 'inherit',
          textDecoration: 'none',
          '&:hover': {
            backgroundColor: theme.palette.brand['600-6%'],
          },
          '&:hover .ask-row-view': { color: 'text.primary' },
        }}
      >
        {inner}
      </Box>
    </Tooltip>
  )
}

/** A row with no entity (country, city, month, day, …) — plain two-column, no avatar, no link. */
const PlainRow = ({ row }: { row: AskResultRow }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
    <Typography variant="body3" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
      {row.label}
    </Typography>
    <Typography variant="body3" sx={{ fontWeight: 600, flexShrink: 0 }}>
      {row.value.toLocaleString()}
    </Typography>
  </Stack>
)

export const AskResultView = ({ result }: { result: AskResultSet }) => {
  const theme = useTheme()
  const { rows, unattributed } = result

  // A single row: the prose already states the one number, so a one-row table beside
  // it is noise. The unattributed caveat is independent of the row count, though — a
  // one-row answer can still be sitting on top of unattributed data the model never
  // saw, so that warning still renders even here.
  const showTable = rows.length > 1

  if (!showTable && !unattributed) return null

  const visibleRows = rows.slice(0, MAX_RENDERED_ROWS)
  const truncated = rows.length > MAX_RENDERED_ROWS
  const rankingLink = showTable ? buildAskDeepLink(result) : null

  return (
    <Stack
      spacing={1}
      sx={{
        px: 3,
        py: showTable ? 2 : 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
      }}
    >
      {showTable && (
        <>
          {rankingLink && (
            <Stack direction="row" justifyContent="flex-end">
              <Link
                component={RouterLink}
                to={rankingLink.to}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  textDecoration: 'none',
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <Typography variant="body4" sx={{ fontWeight: 600 }}>
                  {rankingLink.label}
                </Typography>
                <ArrowUpRightIcon strokeWidth={1} size={12} />
              </Link>
            </Stack>
          )}
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${theme.palette.primary[100]}` }} />}>
            {visibleRows.map((row, i) =>
              row.entity ? (
                <EntityRow key={`${row.entity.kind}-${row.entity.id}-${i}`} row={row} result={result} />
              ) : (
                <PlainRow key={`${row.label}-${i}`} row={row} />
              ),
            )}
          </Stack>
          {truncated && (
            <Typography variant="body4" color="text.secondary">
              Showing top {MAX_RENDERED_ROWS} of {rows.length}
            </Typography>
          )}
        </>
      )}

      {!!unattributed && (
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {unattributed.toLocaleString()} {pluralize(result.metric, unattributed)} could not be
          attributed{result.dimension ? ` to a ${result.dimension}` : ''}.
        </Typography>
      )}
    </Stack>
  )
}
